import { useEffect, useState } from "react";
import type { ObservedDTO } from "../../shared/types";
import { get } from "../lib/api";
import { CostBreakdown } from "./CostBreakdown";
import { Badge } from "./ui";

const dollars = (usd: number) => `$${usd.toFixed(2)}`;

/** "14 runs · $9.80 · 6 delegations in the last 24 h": what a conversation started and spent, said one way everywhere. */
export function spendLine(o: Pick<ObservedDTO, "runs" | "spendUsd" | "delegations">): string {
  return `${o.runs} run${o.runs === 1 ? "" : "s"} · ${dollars(o.spendUsd)} · ${o.delegations} delegation${o.delegations === 1 ? "" : "s"} in the last 24 h`;
}

/** Past twice its usual day, a red tag; otherwise its usual day, quietly. Budgets are watched, never enforced yet. */
export function UsualMark({ observed }: { observed: ObservedDTO }) {
  if (observed.usualUsd === null) return null;
  const usual = `${dollars(observed.usualUsd)}${observed.usualFrom === "platform" ? " (the platform's)" : ""}`;
  return observed.over ? <Badge tone="red">over twice its usual day, {usual}</Badge> : <span className="text-tiny text-faint">usual day {usual}</span>;
}

/** One conversation's last day, read for an admin: on its run pages and its transcript. */
export function ConversationSpend({ conversationId, label = "This conversation" }: { conversationId: string; label?: string }) {
  const [observed, setObserved] = useState<ObservedDTO | null>(null);
  useEffect(() => {
    setObserved(null);
    get<ObservedDTO>(`/api/conversations/${conversationId}/observed`).then(setObserved, () => {});
  }, [conversationId]);
  if (!observed) return null;
  return (
    <span className="inline-flex flex-wrap items-center gap-2 text-xs text-muted tabular-nums">
      <span>{label}: {spendLine(observed)}</span>
      <CostBreakdown billedUsd={observed.billedUsd} estimatedUsd={observed.estimatedUsd} />
      <UsualMark observed={observed} />
    </span>
  );
}
